"use client";

import { useState } from "react";
import { IoClose, IoDownload } from "react-icons/io5";
import {
  MdImage,
  MdVideoFile,
  MdAudioFile,
  MdInsertDriveFile,
} from "react-icons/md";

const FilePreviewModal = ({ file, onClose, selectedConversationType }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const baseURL = process.env.REACT_APP_URL;

  // Determine the file type based on extension
  const getFileType = (extension) => {
    const imageExtensions = [
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".bmp",
      ".webp",
      ".jfif",
    ];
    const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
    const audioExtensions = [".mp3", ".wav", ".ogg", ".m4a"];

    const ext = extension?.toLowerCase();
    if (imageExtensions.includes(ext)) return "image";
    if (videoExtensions.includes(ext)) return "video";
    if (audioExtensions.includes(ext)) return "audio";
    return "document";
  };

  const fileType = getFileType(file?.extension);

  // Get the appropriate download URL based on conversation type
  const getDownloadUrl = () => {
    if (selectedConversationType === "group") {
      return `${baseURL}/GroupChat/Download-File/${file.messageId}`;
    }
    return `${baseURL}/Chats/download/${file.messageId}`;
  };

  // Get the preview URL for images
  const getPreviewUrl = () => {
    if (fileType === "image") {
      return getDownloadUrl();
    }
    return null;
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsDownloading(true);
      const url = getDownloadUrl();
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        file.name || `file_${file.messageId}${file.extension || ""}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // The parent component doesn't need to handle the download since this component already does it
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  // Get appropriate icon for the file type
  const getFileIcon = () => {
    switch (fileType) {
      case "image":
        return <MdImage className="w-16 h-16 text-blue-500" />;
      case "video":
        return <MdVideoFile className="w-16 h-16 text-red-500" />;
      case "audio":
        return <MdAudioFile className="w-16 h-16 text-green-500" />;
      default:
        return <MdInsertDriveFile className="w-16 h-16 text-gray-500" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4 ">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-800 truncate max-w-[80%]">
            {file?.name || `File${file?.extension || ""}`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
          >
            <IoClose className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 p-6 flex items-center justify-center max-h-[70vh] overflow-y-auto">
          {fileType === "image" && !previewError ? (
            <img
              src={getPreviewUrl() || "/placeholder.svg"}
              alt={file?.name || "File preview"}
              className="max-w-full max-h-full object-contain rounded-lg"
              onError={() => setPreviewError(true)}
              onLoad={() => setPreviewError(false)}
            />
          ) : (
            <div className="text-center">
              <div className="flex justify-center mb-4">{getFileIcon()}</div>
              <p className="text-gray-600 mb-4">
                {previewError
                  ? "Preview not available"
                  : `${
                      fileType.charAt(0).toUpperCase() + fileType.slice(1)
                    } preview not supported`}
              </p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={isDownloading}
                title="Download this file"
                className={`inline-flex items-center gap-2 rounded-lg bg-buttonBg px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-buttonBgHover ${
                  isDownloading
                    ? "cursor-not-allowed opacity-50 hover:bg-buttonBg"
                    : ""
                }`}
              >
                <IoDownload size={16} className="shrink-0" />
                {isDownloading ? "Downloading..." : "Download"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
